import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import Footer from "../components/Footer";

const STEPS = [
  {
    number: "01",
    title: "Submit Your Quote Request",
    description:
      "Fill out our detailed online form with your contact information, coverage needs, prior insurance history, and any relevant details. The more you share, the better we can tailor your options.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    number: "02",
    title: "Agent Profile Review",
    description:
      "One of our licensed insurance agents personally reviews your submission. We analyze your risk profile, coverage history, and prior carrier information to identify the most suitable markets for your situation.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
        <polyline points="9 11 12 14 22 4" />
      </svg>
    ),
  },
  {
    number: "03",
    title: "Carrier Market Comparison",
    description:
      "We compare rates and coverage options across our network of top-rated insurance carriers. Our goal is to find the best balance of coverage breadth, pricing, and carrier reputation for your specific risk profile.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <path d="M2 20h20" />
      </svg>
    ),
  },
  {
    number: "04",
    title: "Personalized Quote Presentation",
    description:
      "We present your quote options with clear explanations of coverage terms, limits, deductibles, and pricing. You will understand exactly what you are getting — and exactly what you are not — before making any decisions.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  {
    number: "05",
    title: "Policy Activation & Ongoing Support",
    description:
      "Once you choose your coverage, we handle the paperwork and carrier coordination. Your new policy is set up correctly from day one, and our team remains available for endorsements, renewals, and claims guidance.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
];

const COMMITMENTS = [
  {
    label: "Transparency",
    body: "We walk you through every option with plain-language explanations — no jargon, no pressure.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
  },
  {
    label: "Speed",
    body: "Our streamlined process means you receive quotes faster without sacrificing accuracy or thoroughness.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  {
    label: "Choice",
    body: "We work with multiple carriers so we can always put your interests ahead of any single company.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <path d="M2 20h20" />
      </svg>
    ),
  },
  {
    label: "Continuity",
    body: "Your assigned agent stays with you through policy changes, renewals, and any questions along the way.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

export default function OurProcessPage() {
  usePageTitle("Our Process");
  const navigate = useNavigate();

  return (
    <main className="min-h-screen w-full flex flex-col">

      {/* ── Hero ── */}
      <section className="relative bg-blue-dark overflow-hidden py-24 sm:py-32">
        {/* Radial glow — mirrors WhyChoosePaladin */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,45,181,0.45) 0%, transparent 70%), radial-gradient(ellipse 45% 50% at 100% 100%, rgba(0,20,90,0.3) 0%, transparent 60%)",
          }}
        />
        {/* Grid texture — mirrors WhyChoosePaladin */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 80px), repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 80px)",
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          {/* Badge — mirrors CompanyOverview / PurposeSection pill style */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-1 h-4 bg-white-pure rounded-full opacity-30" />
            <span className="font-cinzel text-white-pure uppercase tracking-[0.2em] text-[11px] opacity-60">
              How We Work
            </span>
            <div className="w-1 h-4 bg-white-pure rounded-full opacity-30" />
          </div>

          <h1 className="font-cinzel font-black text-white-pure tracking-[0.04em] leading-[1.1] mb-6 text-3xl sm:text-4xl lg:text-5xl">
            OUR PROCESS
          </h1>

          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-white-pure opacity-20" />
            <div className="w-1.5 h-1.5 rounded-full bg-white-pure opacity-30" />
            <div className="h-px w-12 bg-white-pure opacity-20" />
          </div>

          <p className="font-constantia italic text-white-pure opacity-55 leading-[1.85] max-w-xl mx-auto text-base sm:text-lg">
            From your first quote request to policy activation, every step is designed around your needs — not ours.
          </p>
        </div>
      </section>

      {/* ── Steps ── */}
      <section className="flex-1 py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-br from-[#EEF2F8] via-[#E8EDF6] to-[#EDF1F9]">
        {/* Faint vertical grid — mirrors CompanyOverview */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(1,46,114,0.04) 0px, rgba(1,46,114,0.04) 1px, transparent 1px, transparent 80px)",
          }}
        />

        <div className="max-w-4xl mx-auto relative">

          {/* Section label */}
          <div className="flex flex-col items-center text-center gap-4 mb-16">
            <div className="flex items-center gap-3">
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
              <span className="font-cinzel text-blue-dark uppercase tracking-[0.2em] text-[11px] opacity-60">
                Step by Step
              </span>
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
            </div>
            <h2 className="font-cinzel font-bold text-blue-dark uppercase tracking-[0.06em] leading-[1.15] text-2xl sm:text-3xl">
              From Request to Policy in Five Steps
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative flex flex-col">
            {/* Vertical connector line — desktop only */}
            <div className="absolute left-9 top-10 bottom-10 w-px bg-gradient-to-b from-[rgba(1,46,114,0.18)] to-[rgba(1,46,114,0.04)] hidden sm:block" />

            {STEPS.map((step, i) => (
              <div
                key={step.number}
                className="relative flex items-start gap-5 sm:gap-8 pb-8 last:pb-0"
              >
                {/* Number + icon circle */}
                <div
                  className={[
                    "flex-shrink-0 w-[72px] h-[72px] rounded-full flex flex-col items-center justify-center z-10",
                    i === 0
                      ? "bg-blue-dark border-2 border-blue-dark shadow-[0_8px_32px_rgba(1,46,114,0.28)]"
                      : "bg-white-pure border-2 border-[rgba(1,46,114,0.15)] shadow-[0_2px_12px_rgba(1,46,114,0.07)]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "font-cinzel font-bold text-[9px] tracking-[0.15em] leading-none mb-0.5",
                      i === 0 ? "text-white-pure opacity-60" : "text-blue-dark opacity-35",
                    ].join(" ")}
                  >
                    {step.number}
                  </span>
                  <span className={i === 0 ? "text-white-pure" : "text-blue-dark"}>
                    {step.icon}
                  </span>
                </div>

                {/* Content card */}
                <div className="flex-1 bg-white-pure rounded-2xl px-6 sm:px-7 py-5 sm:py-6 mt-2 border border-[rgba(1,46,114,0.09)] shadow-[0_4px_20px_rgba(1,46,114,0.06)] transition-shadow duration-200 hover:shadow-[0_8px_30px_rgba(1,46,114,0.1)]">
                  <p className="font-cinzel font-bold text-blue-dark mb-2 text-sm sm:text-[15px] tracking-[0.03em]">
                    {step.title}
                  </p>
                  <div className="h-px w-8 bg-blue-dark opacity-20 mb-3" />
                  <p className="font-constantia text-black-deep opacity-60 leading-relaxed text-sm sm:text-[15px]">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Commitments ── */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-white-pure">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="flex flex-col items-center text-center gap-4 mb-14">
            <div className="flex items-center gap-3">
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
              <span className="font-cinzel text-blue-dark uppercase tracking-[0.2em] text-[11px] opacity-60">
                What Sets Us Apart
              </span>
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
            </div>
            <h2 className="font-cinzel font-bold text-blue-dark uppercase tracking-[0.06em] leading-[1.15] text-2xl sm:text-3xl">
              Our Commitments to Every Client
            </h2>
          </div>

          {/* Cards — mirrors WhyChoosePaladin card layout on light bg */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {COMMITMENTS.map((c) => (
              <div
                key={c.label}
                className="rounded-2xl px-6 py-7 flex flex-col gap-4 bg-gradient-to-br from-[#EEF2F8] to-[#E8EDF6] border border-[rgba(1,46,114,0.09)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(1,46,114,0.1)]"
              >
                {/* Icon circle */}
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-blue-dark bg-[rgba(1,46,114,0.08)]">
                  {c.icon}
                </div>

                <div>
                  <p className="font-cinzel font-bold text-blue-dark uppercase tracking-[0.12em] text-[13px]">
                    {c.label}
                  </p>
                  <div className="h-px w-8 bg-blue-dark opacity-20 mt-2" />
                </div>

                <p className="font-constantia text-black-deep opacity-60 leading-relaxed text-sm">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-blue-dark relative overflow-hidden">
        {/* Grid texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 80px), repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 80px)",
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 40%, rgba(0,45,181,0.45) 0%, transparent 100%)",
          }}
        />

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-1 h-4 bg-white-pure rounded-full opacity-30" />
            <span className="font-cinzel text-white-pure uppercase tracking-[0.28em] text-[11px] opacity-55">
              Ready to Begin?
            </span>
            <div className="w-1 h-4 bg-white-pure rounded-full opacity-30" />
          </div>

          <h2 className="font-cinzel font-black text-white-pure tracking-[0.04em] leading-[1.1] mb-4 text-2xl sm:text-3xl lg:text-4xl">
            Start Your Quote Today
          </h2>

          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-12 bg-white-pure opacity-20" />
            <div className="w-1.5 h-1.5 rounded-full bg-white-pure opacity-30" />
            <div className="h-px w-12 bg-white-pure opacity-20" />
          </div>

          <p className="font-constantia italic text-white-pure opacity-55 leading-relaxed mb-10 text-base sm:text-lg">
            Our process is built around you. Submit your request and let our team do the comparison work so you can focus on what matters.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate("/quote")}
              className="font-cinzel uppercase tracking-[0.18em] text-[10px] rounded-full px-8 py-3.5 bg-white-pure text-blue-dark transition-all duration-300 hover:opacity-90 hover:shadow-[0_8px_28px_rgba(0,0,0,0.15)] w-full sm:w-auto"
            >
              Get a Quote
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="font-cinzel uppercase tracking-[0.18em] text-[10px] rounded-full px-8 py-3.5 border border-[rgba(255,255,255,0.3)] text-white-pure opacity-75 transition-all duration-300 hover:opacity-100 hover:border-[rgba(255,255,255,0.6)] w-full sm:w-auto"
            >
              Contact Our Team
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}